/* eslint-disable prettier/prettier */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { msdyn365Commerce } from '@msdyn365-commerce/core';
// import Cookies from 'js-cookie';
// import { ICookieContext } from '@msdyn365-commerce/core-internal/dist/types/interfaces/context/ICookieContext';

/**
 * Utility functions for managing cookies (browser-only safe)
 */

/**
 * Helper: check if running in browser
 */
const isBrowser = msdyn365Commerce.isBrowser;

/**
 * Sets a session-based cookie
 * @param name - Cookie name
 * @param value - Cookie value
 */
export const setCookie = (name: string, value: string, domain?: string): void => {
    if (!isBrowser) {
        return;
    }
    document.cookie = `${name}=${value};path=/${domain ? `;domain=${domain}` : ''}`;
};

/**
 * Gets a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
    if (!isBrowser) {
        return null;
    }
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        // eslint-disable-next-line security/detect-object-injection
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(nameEQ) === 0) {
            return c.substring(nameEQ.length);
        }
    }
    return null;
};

/**
 * Deletes a cookie by name
 * @param name - Cookie name
 */
export const deleteCookie = (name: string, domain?: string): void => {
    if (!isBrowser) {
        return;
    }
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;${domain ? `domain=${domain};` : ''}`;
};

/**
 * Sets the selected store ID in a cookie (session-based)
 */
export const setSelectedStoreId = (storeId: string): void => {
    setCookie('selectedStoreId', storeId);
};

/**
 * Gets the selected store ID from cookie
 */
export const getSelectedStoreId = (): string | null => {
    return getCookie('selectedStoreId');
};

/**
 * Clears the selected store ID cookie
 */
export const clearSelectedStoreId = (): void => {
    deleteCookie('selectedStoreId');
};

/**
 * Sets the event ID in a cookie (session-based)
 */
export const setEventId = (eventId: string): void => {
    setCookie('eventId', eventId);
};

/**
 * Gets the event ID from cookie
 */
export const getEventId = (): string | null => {
    return getCookie('eventId');
};

export const setDefaultCartId = (cartId?: string): void => {
    if (!isBrowser) {
        return;
    }
    if (cartId) {
        setCookie('_msdyn365___cart_', cartId);
    }
};

// /**
//  * Sets the cart data in a cookie (session-based)
//  */
// export const setCartData = (cartData: any): void => {
//     if (!isBrowser) {
//         return;
//     }

//     setCookie('cartData', JSON.stringify(cartData));
// };

// /**
//  * Gets the cart data from cookie
//  */
// export const getCartData = (): any | null => {
//     if (!isBrowser) {
//         return null;
//     }
//     const cartDataString = getCookie('cartData');
//     if (cartDataString) {
//         try {
//             return JSON.parse(cartDataString);
//         } catch (error) {
//             console.error('Error parsing cart data from cookie:', error);
//             return null;
//         }
//     }
//     return null;
// };

// /**
//  * Clears the cart data from cookie
//  */
// export const clearCartData = (): void => {
//     deleteCookie('cartData');
// };

/**
 * Sets the cart data in localStorage
 */
export const setCartData = (cartData: any): void => {
    if (!isBrowser) {
        return;
    }

    try {
        localStorage.setItem('cartData', JSON.stringify(cartData));
    } catch (error) {
        console.error('Failed to set cart data in localStorage:', error);
    }
};

/**
 * Gets the cart data from localStorage
 */
export const getCartData = (): any | null => {
    if (!isBrowser) {
        return null;
    }

    try {
        const cartDataString = localStorage.getItem('cartData');
        return cartDataString ? JSON.parse(cartDataString) : null;
    } catch (error) {
        console.error('Error parsing cart data from localStorage:', error);
        return null;
    }
};

/**
 * Clears the cart data from localStorage
 */
export const clearCartData = (): void => {
    if (!isBrowser) {
        return;
    }

    try {
        localStorage.removeItem('cartData');
    } catch (error) {
        console.error('Failed to remove cart data from localStorage:', error);
    }
};

// export const setNewCartPosData = (cartData: any): void => {
//     if (!isBrowser) {return;}
//     try {
//         Cookies.set('_msdyn365___cart_pos_data', JSON.stringify(cartData), { path: '/' });
//     } catch (error) {
//         console.error("Failed to set cart data cookie:", error);
//     }
// };

// export const getNewCartPosData = (): any | null => {
//     if (!isBrowser) {return null;}
//     try {
//         const cookie = Cookies.get('_msdyn365___cart_pos_data');
//         return cookie ? JSON.parse(cookie) : null;
//     } catch (error) {
//         console.error("Failed to parse cart data cookie:", error);
//         return null;
//     }
// };

export const setNewCartPosData = (cartData: any): void => {
    if (!isBrowser) {
        return;
    }
    try {
        localStorage.setItem('_msdyn365___cart_pos_data', JSON.stringify(cartData));
    } catch (error) {
        console.error('Failed to set cart data in localStorage:', error);
    }
};

export const getNewCartPosData = (): any | null => {
    if (!isBrowser) {
        return null;
    }
    try {
        const data = localStorage.getItem('_msdyn365___cart_pos_data');
        return data ? JSON.parse(data) : null;
    } catch (error) {
        console.error('Failed to parse cart data from localStorage:', error);
        return null;
    }
};

export const removeNewCartPosData = (): void => {
    if (!isBrowser) {
        return;
    }
    try {
        localStorage.removeItem('_msdyn365___cart_pos_data');
    } catch (error) {
        console.error('Failed to remove cart data from localStorage:', error);
    }
};

export const setTableId = (tableId: string): void => {
    setCookie('tableId', tableId);
};

export const getTableId = (): string | null => {
    return getCookie('tableId');
};

/**
 * Clears the table ID cookie
 */
export const clearTableId = (): void => {
    deleteCookie('tableId');
};

export const setCustomerId = (customerId: string): void => {
    setCookie('customerId', customerId);
};

export const getCustomerId = (): string | null => {
    return getCookie('customerId');
};

export const setToken = (token: string): void => {
    setCookie('token', token);
};

export const setSelectedMenuId = (menuId: string): void => {
    setCookie('selectedMenuId', menuId);
};

export const getSelectedMenuId = (): string | null => {
    return getCookie('selectedMenuId');
};

export const setNBAId = (nbaId: string): void => {
    setCookie('nbaId', nbaId);
};

export const getNBAId = (): string | null => {
    return getCookie('nbaId');
};

export const setIsSingleRental = (isSingleRental: boolean): void => {
    setCookie('singleNightRental', isSingleRental.toString());
};

export const getIsSingleNightRental = (): string | null => {
    return getCookie('singleNightRental');
};

/**
 * The session is shared with the app the guest arrives from and returns to, in cookies that app scopes to
 * the domain both sites sit under. Writing without that scope does not update the shared cookie: it makes a
 * second one of the same name at this host, which the browser sends alongside the first. Reads then answer
 * with whichever is listed first, and the shared copy keeps the refresh token the identity provider revoked
 * when this site refreshed - so the guest who navigates back arrives with a dead session.
 *
 * The scope is authored, because only the parent app knows where it puts them and it cannot be read back
 * from script. Without it the shared cookie cannot be reached and this site writes at its own host, which
 * is what it did before.
 */
export const setAccessToken = (accessToken: string, sessionCookieDomain?: string): void => {
    setCookie('accessToken', accessToken, sessionCookieDomain);
};

export const getAccessToken = (): string | null => {
    return getCookie('accessToken');
};

export const setRefreshToken = (refreshToken: string, sessionCookieDomain?: string): void => {
    setCookie('refreshToken', refreshToken, sessionCookieDomain);
};

export const getRefreshToken = (): string | null => {
    return getCookie('refreshToken');
};

/**
 * Clears the copy this host owns, left behind by writes made before the shared scope was authored. Sent
 * without a domain it can only reach that copy, so the shared one is left as it is. Only safe to call once
 * the shared copy has been written, and it is the write that makes the remaining copy the current one.
 */
export const retireHostOnlySessionTokens = (): void => {
    deleteCookie('accessToken');
    deleteCookie('refreshToken');
};

export const getGlobantSuiteId = (): string | null => {
    return getCookie('GlobantSuiteId');
};

/** @deprecated Use getGlobantSuiteId — kept for existing imports with the old typo. */
export const getGlobandSuiteId = getGlobantSuiteId;

export const setGlobandSuiteId = (globantSuiteId: string): void => {
    setCookie('GlobantSuiteId', globantSuiteId);
};

/** Kept once the Fan App's is-mobile cookie has been seen, so a later full page load in the session still has it. */
const IS_MOBILE_SEEN_KEY = 'hs-is-mobile-seen';

/** The Fan App's is-mobile cookie, or '1' for the rest of the session once it has been seen. */
export const getIsMobileCookie = (): string | null => {
    const cookie = getCookie('is-mobile');
    if (!isBrowser) {
        return cookie;
    }

    try {
        if (cookie === '1') {
            window.sessionStorage.setItem(IS_MOBILE_SEEN_KEY, '1');
            return cookie;
        }

        return window.sessionStorage.getItem(IS_MOBILE_SEEN_KEY) === '1' ? '1' : cookie;
    } catch (error) {
        return cookie;
    }
};

// Helper function to build auth token object from separate cookies
export const getAuthToken = () => ({
    IntuitDomeAccountId: getNBAId() || '',
    Token: getAccessToken() || '',
    RefreshToken: getRefreshToken() || '',
    SuiteId: getGlobantSuiteId() || '',
    EventId: getEventId() || ''
});

export const setSuiteDescription = (suiteDescription: string): void => {
    setCookie('suiteDescription', suiteDescription);
};

export const getSuiteDescription = (): string | null => {
    return getCookie('suiteDescription');
};
